/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { Button, Icon } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Close } from '@adobe-commerce/elsie/icons';
import { VComponent, classes } from '@adobe-commerce/elsie/lib';
import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';

import '@adobe-commerce/elsie/components/InLineAlert/InLineAlert.css';
export interface InLineAlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'icon'> {
  variant?: 'primary' | 'secondary';
  heading: string;
  description?: string;
  type?: 'error' | 'warning' | 'success';
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  additionalActions?: Array<{
    label: string;
    onClick: (event: Event) => void;
    'aria-label'?: string;
  }>;
  onDismiss?: (event: Event) => void;
  itemList?: VNode;
  actionButtonPosition?: 'top' | 'bottom';
}

export const InLineAlert: FunctionComponent<InLineAlertProps> = ({
  variant = 'primary',
  className,
  type = 'warning',
  additionalActions,
  onDismiss,
  heading,
  description,
  icon,
  itemList,
  actionButtonPosition,
  ...props
}) => {
  const translations = useText({
    dismiss: 'Dropin.InlineAlert.dismissLabel',
  });

  return (
    <div
      {...props}
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      className={classes([
        'dropin-in-line-alert',
        `dropin-in-line-alert--${type}`,
        `dropin-in-line-alert--${variant}`,
        className,
      ])}
    >
      <div className={'dropin-in-line-alert__heading'}>
        <div className={'dropin-in-line-alert__title-container'}>
          {icon && (
            <VComponent node={icon} className={'dropin-in-line-alert__icon'} />
          )}

          <span className={'dropin-in-line-alert__title'}>{heading}</span>
        </div>

        <div className={'dropin-in-line-alert__actions-container'}>
          {additionalActions &&
            (actionButtonPosition === 'top' ||
              (!actionButtonPosition && additionalActions.length <= 1)) && (
              <Button
                variant="tertiary"
                className={'dropin-in-line-alert__additional-action'}
                onClick={
                  additionalActions.length > 0
                    ? additionalActions[0]?.onClick
                    : undefined
                }
                aria-label={
                  additionalActions[0]?.['aria-label'] ??
                  additionalActions[0]?.label
                }
              >
                {additionalActions[0]?.label}
              </Button>
            )}
          {onDismiss && (
            <Button
              icon={<Icon source={Close} size="24" stroke="2" />}
              className={'dropin-in-line-alert__dismiss-button'}
              variant="tertiary"
              onClick={onDismiss}
              aria-label={translations.dismiss}
            />
          )}
        </div>
      </div>

      {description && (
        <p className={'dropin-in-line-alert__description'}>{description}</p>
      )}
      <div className={'dropin-in-line-alert__item-list-container'}>
        {itemList && (
          <VComponent
            node={itemList}
            className={classes(['dropin-in-line-alert__item-list'])}
          />
        )}
      </div>
      {additionalActions &&
        (actionButtonPosition === 'bottom' ||
          (!actionButtonPosition && additionalActions.length > 1)) && (
          <div className={'dropin-in-line-alert__additional-actions-container'}>
            {additionalActions.map((action) => (
              <Button
                key={action.label}
                variant="tertiary"
                className={'dropin-in-line-alert__additional-action'}
                onClick={action.onClick}
                aria-label={action['aria-label'] ?? action.label}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
    </div>
  );
};
